import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/special/Header';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useState } from 'react';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { hasPermissions } from '../../auth/hasPermissions';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import { Toolbar } from '../../components/exo/project/Toolbar';
import { NewSalesObject } from '../../components/new/NewSalesObject';
import ExoDialog from '../../components/exo/ExoDialog';
import { ProjectAddElementButton } from '../../components/exo/project/ProjectAddElementButton';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../../components/exo/ExoAvatar';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EmailIcon from '@mui/icons-material/Email';
import { SalesObjectLevel } from './level/SalesObjectLevel';
import { NewSalesLevel } from '../../components/new/NewSalesLevel';
import { updateOrCreate } from '../../components/special/updateOrCreate';

export const ShowSalesObject = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { apiData, setApiData } = useFetch(`sales-objects/${id}`);
  const [open, setOpen] = useState(false);
  const [openNewLevel, setOpenNewLevel] = useState(false);
  const [editLevel, setEditLevel] = useState(null);

  // object management
  const [inEditMode, setInEditMode] = useState(false);
  const toollist = [
    {
      Icon: SettingsApplicationsIcon,
      label: t('Settings'),
      onClick: () => setOpen(true),
      hidden: !hasPermissions('sales-object', 2),
    },
    {
      Icon: BarChartIcon,
      label: t('Analytics'),
      onClick: () => navigate(`/sales-objects/${id}/statistics`),
      hidden: true || !hasPermissions('sales-object-statistics', 2),
    },
  ];

  function handleLevelChange(newLevel) {
    setOpenNewLevel(false);
    const newLevels = updateOrCreate(apiData.data.levels, newLevel.data);
    setApiData({ ...apiData, data: { ...apiData.data, levels: newLevels } });
  }

  if (!apiData)
    return (
      <>
        <ExoSkeleton size={'38px'} />
      </>
    );

  const { title, image, owner, supervisor, levels, accessPoints, status, nr } =
    apiData.data;

  return (
    <>
      <Header
        title={
          <>
            {image && (
              <img
                className=" h-10 w-20 object-cover rounded-[10px]"
                src={image.temporaryUrl}
              />
            )}
            {title}
          </>
        }
      />
      {hasPermissions('sales-object', 2) && (
        <Toolbar
          tools={toollist}
          setInEditMode={setInEditMode}
          inEditMode={inEditMode}
        />
      )}
      <Box className="flex flex-col justify-between h-full gap-2 overflow-y-auto">
        <Box className="w-full h-full flex flex-col gap-4">
          {levels.map((lvl) => (
            <SalesObjectLevel
              key={lvl.id}
              data={lvl}
              onChange={handleLevelChange}
            />
          ))}
          {inEditMode && (
            <ProjectAddElementButton
              label={t('Add Level')}
              onClick={() => setOpenNewLevel(true)}
            />
          )}
        </Box>
        <Box
          className=" p-4 rounded-lg mt-4 backdrop-blur-lg flex "
          sx={{ bgcolor: colors.glass }}
        >
          <Box>
            <Typography variant="h6" className="pb-2">
              {t('Object supervisor')}
            </Typography>
            <Box className="flex gap-4">
              <Box className="flex flex-col">
                <Typography
                  variant="h4"
                  className="flex items-center gap-2 pb-1"
                >
                  <ExoAvatar
                    picture={
                      supervisor.picture && supervisor.picture.temporaryUrl
                    }
                    name={`${supervisor.firstName} ${supervisor.lastName}`}
                    size="30px"
                  />
                  {`${supervisor.salutation} ${supervisor.firstName} ${supervisor.lastName}`}
                </Typography>

                <Box className="flex items-center gap-1">
                  <Link href={`tel:${supervisor.phone}`}>
                    <IconButton size="small">
                      <PhoneIphoneIcon />
                    </IconButton>
                  </Link>
                  <Typography>{supervisor.phone}</Typography>
                </Box>

                <Box className="flex items-center gap-1">
                  <Link href={`mailto:${supervisor.email}`}>
                    <IconButton size="small">
                      <EmailIcon />
                    </IconButton>
                  </Link>
                  <Typography>{supervisor.email}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <ExoDialog open={open} limitWidth onClose={() => setOpen(false)}>
        <NewSalesObject
          startData={apiData.data}
          onUpdate={(data) => {
            setOpen(false);
            setApiData(data);
          }}
          onClose={() => setOpen(false)}
        />
      </ExoDialog>
      <ExoDialog
        open={openNewLevel}
        limitWidth
        onClose={() => setOpenNewLevel(false)}
      >
        <NewSalesLevel
          startData={editLevel}
          onUpdate={handleLevelChange}
          onCreate={handleLevelChange}
          onClose={() => setOpenNewLevel(false)}
          salesObjectId={id}
        />
      </ExoDialog>
    </>
  );
};
